import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/hooks/use-query-client";
import {
  APP_ALERT_MODULE,
  DATA_ACCESS_ACTION_BUTTON_LABEL,
  DATA_ACCESS_ALL_STATUS,
  DATA_ACCESS_STATUS_LABEL,
  DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER,
  QueryKeys,
  SELF_DATA_ACCESS_STATUS_LABEL,
} from "@/constants";
import type { IDialogBox } from "../header";
import type { Demographics, Relation } from "@/model/demographics";
import {
  capitalizeInitialLetter,
  lowerCaseComparison,
} from "@/lib/string-util";
import { hasDataAccess } from "@/lib/data-access-utils";
import type { DataAccessListItem } from "@/model/data-access";
import type { HasDataAccessStatusType } from "@/types";
import useAuth, { AuthMode } from "@/context/auth-provider";
import { useDispatch } from "react-redux";
import { setAppAlertDialogs } from "@/store/app-alert-dialog/app-alert-dialog-slice";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import IDENTIFIERS from "@/constants/test_id";

const DataAccessManagement: React.FC<IDialogBox> = ({
  dialogOpen,
  setDialogOpen,
  logDialogName,
  logTrigger,
}) => {
  const { authData } = useAuth();
  const dispatch = useDispatch();
  const dependents = queryClient.getQueryData([
    QueryKeys.DEPENDENTS,
  ]) as Relation[];
  const authorizationAccessList = queryClient.getQueryData([
    QueryKeys.DATA_ACCESS_LIST,
  ]);
  const demographics = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics;

  const renderSelfDataAccessToDepedentStatusLabel = (dependent: Relation) => {
    if ((authorizationAccessList as DataAccessListItem[]).length === 0) {
      return DATA_ACCESS_STATUS_LABEL.NOT_SHARED;
    }
    const dataAccessObj = hasDataAccess(
      dependent?.memberId,
      authorizationAccessList as DataAccessListItem[],
    );

    if (!(dataAccessObj as HasDataAccessStatusType).hasAccess) {
      return DATA_ACCESS_STATUS_LABEL.NOT_SHARED;
    }

    return (dataAccessObj as HasDataAccessStatusType).status ===
      DATA_ACCESS_ALL_STATUS.GRANTED
      ? DATA_ACCESS_STATUS_LABEL.PENDING
      : DATA_ACCESS_STATUS_LABEL.SHARED;
  };

  const renderDependentDataAccessToSelfStatusLabel = (dependent: Relation) => {
    if (!dependent?.hipaaProtected) {
      return SELF_DATA_ACCESS_STATUS_LABEL.SHARED;
    }

    if ((authorizationAccessList as DataAccessListItem[]).length === 0) {
      return SELF_DATA_ACCESS_STATUS_LABEL.NOT_SHARED;
    }

    const dataAccessObj = hasDataAccess(
      dependent.memberId,
      authorizationAccessList as DataAccessListItem[],
      { selfMemberId: demographics.memberId },
    );

    if (!(dataAccessObj as HasDataAccessStatusType).hasAccess) {
      return SELF_DATA_ACCESS_STATUS_LABEL.NOT_SHARED;
    }

    return (dataAccessObj as HasDataAccessStatusType)?.status ===
      DATA_ACCESS_ALL_STATUS.GRANTED
      ? SELF_DATA_ACCESS_STATUS_LABEL.PENDING
      : SELF_DATA_ACCESS_STATUS_LABEL.SHARED;
  };

  const renderDependentDataActionButtonLabel = (dependent: Relation) => {
    if ((authorizationAccessList as DataAccessListItem[]).length === 0) {
      return DATA_ACCESS_ACTION_BUTTON_LABEL.AUTHORIZE_ACCESS;
    } else {
      const dataAccessObj = hasDataAccess(
        dependent.memberId,
        authorizationAccessList as DataAccessListItem[],
      );
      if ((dataAccessObj as HasDataAccessStatusType).hasAccess) {
        return DATA_ACCESS_ACTION_BUTTON_LABEL.REVOKE_ACCESS;
      } else {
        return DATA_ACCESS_ACTION_BUTTON_LABEL.AUTHORIZE_ACCESS;
      }
    }
  };

  const onDataAccessAction = (dependent: Relation) => {
    setDialogOpen(false);
    if ((authorizationAccessList as DataAccessListItem[]).length > 0) {
      const dataAccessObj = hasDataAccess(
        dependent.memberId,
        authorizationAccessList as DataAccessListItem[],
      );
      if ((dataAccessObj as HasDataAccessStatusType).hasAccess) {
        const appAlertDialog = {
          grantedBy: demographics?.memberId,
          grantedTo: dependent.memberId,
          contentType: DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.AUTH_REVOKE,
          module: APP_ALERT_MODULE.DATA_ACCESS,
        };
        dispatch(setAppAlertDialogs([appAlertDialog]));
      } else {
        const appAlertDialog = {
          grantedBy: demographics?.memberId,
          grantedTo: dependent.memberId,
          contentType: DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.AUTH_GRANT,
          module: APP_ALERT_MODULE.DATA_ACCESS,
        };
        dispatch(setAppAlertDialogs([appAlertDialog]));
      }
    } else {
      const appAlertDialog = {
        grantedBy: demographics?.memberId,
        grantedTo: dependent.memberId,
        contentType: DATA_ACCESS_STATUS_TO_ALERT_TYPE_MAPPER.AUTH_GRANT,
        module: APP_ALERT_MODULE.DATA_ACCESS,
      };
      dispatch(setAppAlertDialogs([appAlertDialog]));
    }
  };

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(open) => setDialogOpen(open)}
      logDialogName={logDialogName}
      logTrigger={logTrigger}
      logTag={LogTags.MODULE_ACCESS}
      logCategory={LogCategories.DATA_ACCESS_MANAGEMENT}
      logSubCategory={LogSubCategories.NONE}
    >
      <DialogContent
        className="sm:max-w-[500px]"
        data-hj-suppress
        logDialogName={logDialogName}
      >
        <DialogHeader>
          <DialogTitle>Data Access Management</DialogTitle>
          <DialogDescription>Manage your data access</DialogDescription>
        </DialogHeader>
        <ul className="space-y-6">
          {dependents && authorizationAccessList
            ? dependents.map((dependent) => (
                <li key={dependent.memberId} className="flex justify-between">
                  <div className="text-gray-900">
                    <div className="text-medium">
                      {dependent.firstName} {dependent.lastName}{" "}
                      {/*Implementation as requested by https://zakipointhealth.atlassian.net/browse/CSRTB-253 */}
                      {`(${capitalizeInitialLetter(dependent?.relationship?.toLowerCase() === "employee" ? "Subscriber" : dependent?.relationship || "")})`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {renderSelfDataAccessToDepedentStatusLabel(dependent)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {renderDependentDataAccessToSelfStatusLabel(dependent)}
                    </div>
                  </div>
                  {dependent?.hipaaProtected ? (
                    <div className="flex items-center">
                      <Button
                        variant="outline"
                        className="mb-1 border-primary bg-gray-50 font-normal text-primary"
                        onClick={() => onDataAccessAction(dependent)}
                        disabled={lowerCaseComparison(
                          authData?.mode,
                          AuthMode.GUEST,
                        )}
                        data-testid={
                          IDENTIFIERS.DATA_ACCESS_MANAGEMENT.ACTION_BUTTON
                        }
                      >
                        {renderDependentDataActionButtonLabel(dependent)}
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))
            : null}
        </ul>
        <div className="mt-4">
          <span className="text-medium">
            *(Relationships are based on subscriber)
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataAccessManagement;
